/**
 * ============================================================================
 * PromotionsTable - Main Promotions Table Component
 * ============================================================================
 */

import React, { useState } from 'react';
import {
  Download,
  Search,
  Calendar,
  Clock,
  Percent,
  Tag as TagIcon,
  Power,
  Trash2,
  CheckCircle,
  XCircle,
  Edit2,
  BarChart3,
} from 'lucide-react';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import {
  formatDateRange,
  formatTimeRange,
  formatDaysOfWeek,
  getPromotionStatus,
  formatDiscount,
  exportPromotionsToCSV,
} from '../../utils/promotionFormatters';
import type { Promotion, PromotionFilters } from '../../types/promotions';
import { useTogglePromotion, useDeletePromotion } from '../../hooks/usePromotions';

interface PromotionsTableProps {
  promotions: Promotion[];
  filters: PromotionFilters;
  onFilterChange: (filters: Partial<PromotionFilters>) => void;
  venueId: number;
  onEdit?: (promotion: Promotion) => void;
}

function StatusBadge({ promotion }: { promotion: Promotion }) {
  const { label, colorClass } = getPromotionStatus(promotion);
  const icons = {
    active: CheckCircle,
    scheduled: Clock,
    expired: XCircle,
    inactive: XCircle,
  };
  const Icon = icons[getPromotionStatus(promotion).status];

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${colorClass}`}
      role="status"
      aria-label={`Status: ${label}`}
    >
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </span>
  );
}

export function PromotionsTable({
  promotions,
  filters,
  onFilterChange,
  venueId,
  onEdit,
}: PromotionsTableProps) {
  const { confirm, alert } = useConfirmation();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleMutation = useTogglePromotion();
  const deleteMutation = useDeletePromotion();

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPromotions = promotions.slice(startIndex, endIndex);
  const totalPages = Math.ceil(promotions.length / itemsPerPage);

  // Export handler
  const handleExport = async () => {
    try {
      exportPromotionsToCSV(promotions);
    } catch (error) {
      await alert({
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export promotions.',
        variant: 'danger',
      });
    }
  };

  // Toggle handler
  const handleToggle = async (promotionId: number) => {
    const confirmed = await confirm({
      title: 'Toggle Promotion Status',
      message: 'Are you sure you want to toggle this promotion status?',
      confirmLabel: 'Toggle',
      cancelLabel: 'Cancel',
      variant: 'warning',
    });
    if (confirmed) {
      toggleMutation.mutate({ venueId, promotionId });
    }
  };

  // Delete handler
  const handleDelete = async (promotionId: number) => {
    const confirmed = await confirm({
      title: 'Delete Promotion',
      message: 'Are you sure you want to delete this promotion? This action cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (confirmed) {
      deleteMutation.mutate({ venueId, promotionId });
    }
  };

  return (
    <div>
      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 sm:max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search promotions..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full text-sm"
                aria-label="Search promotions"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => onFilterChange({ status: e.target.value as any })}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 outline-none focus:border-blue-500"
              aria-label="Filter by status"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="scheduled">Scheduled</option>
              <option value="expired">Expired</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={promotions.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Promotion
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Discount
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Valid Period
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Schedule
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Sport
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Usage
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
          <tbody>
            {paginatedPromotions.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  No promotions found
                </td>
              </tr>
            ) : (
              paginatedPromotions.map((promo) => (
                <tr
                  key={promo.id}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{promo.title}</p>
                      {promo.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                          {promo.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                        <Percent className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-black text-orange-600">
                        {formatDiscount(promo.discount_percentage)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm text-gray-700">
                      <Calendar className="w-3 h-3 mr-2 text-gray-400" />
                      {formatDateRange(promo.valid_from, promo.valid_until)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="flex items-center text-xs text-gray-600">
                        <Clock className="w-3 h-3 mr-1 text-gray-400" />
                        {formatTimeRange(promo.start_time, promo.end_time)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {formatDaysOfWeek(promo.days_of_week)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">
                      {promo.sport?.name || 'All Sports'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-center">
                      <div
                        className="flex items-center text-gray-400 cursor-help"
                        title="Usage tracking coming soon - will show total bookings using this promotion"
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        <span className="text-xs font-semibold">N/A</span>
                      </div>
                      <span className="text-[10px] text-gray-400 mt-0.5">Coming Soon</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge promotion={promo} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      {onEdit && (
                        <button
                          onClick={() => onEdit(promo)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                          aria-label="Edit promotion"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggle(promo.id)}
                        disabled={toggleMutation.isPending}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                        title={promo.is_active ? 'Deactivate' : 'Activate'}
                        aria-label={promo.is_active ? 'Deactivate promotion' : 'Activate promotion'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(promo.id)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        title="Delete"
                        aria-label="Delete promotion"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination - Desktop */}
        {paginatedPromotions.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-semibold">
                {startIndex + 1}-{Math.min(endIndex, promotions.length)}
              </span>{' '}
              of <span className="font-semibold">{promotions.length}</span> promotions
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label={`Page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedPromotions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            No promotions found
          </div>
        ) : (
          paginatedPromotions.map((promo) => (
            <div
              key={promo.id}
              className="bg-white rounded-xl border border-gray-200 p-3 space-y-3"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{promo.title}</h3>
                  {promo.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {promo.description}
                    </p>
                  )}
                </div>
                <StatusBadge promotion={promo} />
              </div>

              {/* Discount */}
              <div className="flex items-center">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                  <Percent className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Discount</p>
                  <p className="text-sm font-black text-orange-600">
                    {formatDiscount(promo.discount_percentage)}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="flex items-center text-gray-400 mb-0.5">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span className="text-[10px] uppercase tracking-wide">Valid Period</span>
                  </div>
                  <p className="text-gray-700 font-medium">
                    {formatDateRange(promo.valid_from, promo.valid_until)}
                  </p>
                </div>
                <div>
                  <div className="flex items-center text-gray-400 mb-0.5">
                    <Clock className="w-3 h-3 mr-1" />
                    <span className="text-[10px] uppercase tracking-wide">Time</span>
                  </div>
                  <p className="text-gray-700 font-medium">
                    {formatTimeRange(promo.start_time, promo.end_time)}
                  </p>
                </div>
                <div>
                  <div className="flex items-center text-gray-400 mb-0.5">
                    <TagIcon className="w-3 h-3 mr-1" />
                    <span className="text-[10px] uppercase tracking-wide">Sport</span>
                  </div>
                  <p className="text-gray-700 font-medium truncate">
                    {promo.sport?.name || 'All Sports'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center text-gray-400 mb-0.5">
                    <BarChart3 className="w-3 h-3 mr-1" />
                    <span className="text-[10px] uppercase tracking-wide">Usage</span>
                  </div>
                  <p className="text-gray-400 font-medium text-xs">Coming Soon</p>
                </div>
              </div>

              {/* Days */}
              <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">
                <span className="font-semibold">Days: </span>
                {formatDaysOfWeek(promo.days_of_week)}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                {onEdit && (
                  <button
                    onClick={() => onEdit(promo)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Edit"
                    aria-label="Edit promotion"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleToggle(promo.id)}
                  disabled={toggleMutation.isPending}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                  title={promo.is_active ? 'Deactivate' : 'Activate'}
                  aria-label={promo.is_active ? 'Deactivate promotion' : 'Activate promotion'}
                >
                  <Power className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(promo.id)}
                  disabled={deleteMutation.isPending}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  title="Delete"
                  aria-label="Delete promotion"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}

        {/* Pagination - Mobile */}
        {paginatedPromotions.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col gap-3">
            <p className="text-xs text-gray-600 text-center">
              Showing{' '}
              <span className="font-semibold">
                {startIndex + 1}-{Math.min(endIndex, promotions.length)}
              </span>{' '}
              of <span className="font-semibold">{promotions.length}</span>
            </p>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Prev
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                      aria-label={`Page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
